<script>
import { mapGetters } from 'vuex'
import { aawzDate } from '@utils/dates/aawz-date'
import { GlobalBus } from '@services/bus'
import { MODAL, FEATURES, RELOAD } from '@constants/Text'
import {
  ICONS,
  TASK_FROM_MESSAGE,
  NOTES_SECTION,
  HISTORY_SCROLL,
  IMPORT_ANTERIORES_BATCH,
} from '@modules/atendimento/constants/Text'
import { parseBackendDate } from '@modules/atendimento/utils/message-formatter'
import {
  buildTaskTitleFromMessage,
  buildTaskDescriptionFromMessage,
} from '@modules/atendimento/utils/task-from-message'
import {
  resolveClienteAppIdFromConversa,
  resolveResponsavelFromConversa,
} from '@modules/atendimento/utils/task-from-conversa'
import { buildDefaultTaskDates } from '@modules/tarefas/utils/taskDefaultDates'
import apiAtendimento from '@modules/atendimento/api/apiAtendimento'
import { apiAawzDrive } from '@modules/aawz-drive/api/apiAawzDrive'
import { uploadMessageMediaToFolder } from '@modules/atendimento/services/atendimento-drive.service'
import {
  detectMediaType,
  buildMediaMessagePayload,
} from '@modules/atendimento/utils/media-utils'
import ModalProfilePicture from '@modules/atendimento/modal/modal-profile-picture/modal-profile-picture.vue'
import ChatWindowHeader from '@modules/atendimento/components/chat-window-header/chat-window-header.vue'
import ChatWindowMessages from '@modules/atendimento/components/chat-window-messages/chat-window-messages.vue'
import ChatInput from '@modules/atendimento/components/chat-input/chat-input.vue'
import ChatWindowEmptyState from '@modules/atendimento/components/chat-window-empty-state/chat-window-empty-state.vue'
import { resolveConexaoStatusCta } from '@modules/atendimento/utils/conexao-status-message'

export default {
  name: 'ChatWindow',
  components: {
    ChatWindowHeader,
    ChatWindowMessages,
    ChatInput,
    ChatWindowEmptyState,
    ModalProfilePicture,
  },
  props: {
    hasConexoes: {
      type: Boolean,
      default: false,
    },
    conexoes: {
      type: Array,
      default() {
        return []
      },
    },
    showCloseConversa: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: ICONS,
      messageText: '',
      sending: false,
      loadingOlder: false,
      // Assume que há mais histórico até a API dizer o contrário (has_more:
      // false na primeira página). Com hasMore=false de largada, o gate em
      // onScrollMessages bloquearia a primeira tentativa de carregar antigos.
      hasMore: true,
      // Assume que o WhatsApp (Evolution) ainda tem mensagens anteriores até o
      // import retornar imported=0 / has_more_whatsapp=false. Quando o banco
      // esgota (hasMore=false), o scroll-até-o-topo importa o lote anterior
      // enquanto este flag for true.
      hasMoreWhatsapp: true,
      // True enquanto importa do WhatsApp (troca o label do spinner). Separado
      // de loadingOlder (que também cobre a paginação do banco) para o label.
      importingWhatsapp: false,
      loadingAction: false,
      // Rascunho de mensagem por conversa (id → texto). Em-memória — não
      // persiste no backend nem entre reloads, evita texto vazar entre conversas.
      drafts: {},
    }
  },
  computed: {
    ...mapGetters('atendimento', [
      'mensagens',
      'selectedConversa',
      'uploadProgress',
      'loadingMensagens',
    ]),
    // Leitura defensiva do getter (pode não estar registrado em alguns
    // mocks de teste); fallback para objeto vazio mantém o template seguro.
    linkedTaskByMessageId() {
      const getters = this.$store && this.$store.getters
      const map = getters && getters['atendimento/linkedTaskByMessageId']
      return map || {}
    },
    // has_more da CARGA INICIAL das mensagens (store, escrito pela action
    // listarMensagens sem before_id). É a fonte de verdade para o 1º scroll:
    // se a conversa já veio esgotada do banco (false), o hasMore local deve
    // refletir isso e o 1º scroll cai direto no import do WhatsApp. Leitura
    // defensiva (getter pode não estar registrado em mocks) com default true.
    initialHasMore() {
      const getters = this.$store && this.$store.getters
      const value = getters && getters['atendimento/mensagensHasMore']
      return value !== false
    },
    taskFeatureEnabled() {
      return this.$access[FEATURES.TASK_V2] === true
    },
    hasConversa() {
      return !!this.selectedConversa
    },
    // Id do cliente vinculado à conversa aberta — rootKey da árvore do Drive
    // (pré-fetch + destino do upload) e base do gate hasLinkedClient.
    idClienteApp() {
      return resolveClienteAppIdFromConversa(this.selectedConversa)
    },
    // Cliente vinculado à conversa aberta — habilita "Enviar para AAWZ Drive"
    // nas ações de mensagem (repassado pela cadeia de componentes de mensagem).
    hasLinkedClient() {
      return !!this.idClienteApp
    },
    isGestor() {
      const user = this.$currentUser() || {}
      return !!user.IS_GESTOR
    },
    conexaoCta() {
      return resolveConexaoStatusCta({
        conexoes: this.conexoes,
        isGestor: this.isGestor,
      })
    },
    visibleMensagens() {
      return this.mensagens || []
    },
    firstMessageId() {
      if (!this.mensagens || this.mensagens.length === 0) return null
      return this.mensagens[0].id
    },
    // Timestamp (epoch SEGUNDOS) da mensagem mais ANTIGA já carregada. A lista
    // está em ordem ascendente (PREPEND_MENSAGENS re-ordena por data_mensagem),
    // então mensagens[0] é a mais antiga. Cursor `before_ts` do import do
    // WhatsApp: pedimos as mensagens anteriores a este instante. Null quando
    // não há mensagem ou a data é inválida.
    oldestMessageTs() {
      if (!this.mensagens || this.mensagens.length === 0) return null
      const oldest = this.mensagens[0]
      const date = parseBackendDate(oldest && oldest.data_mensagem)
      if (!date) return null
      const ts = aawzDate(date).unix()
      return Number.isNaN(ts) ? null : ts
    },
    // Verdadeiro quando não há mais histórico em lugar nenhum (banco e WhatsApp
    // esgotados). Só então mostramos o indicador "Início da conversa".
    beginningOfConversation() {
      return !this.hasMore && !this.hasMoreWhatsapp
    },
    // Label do spinner de carregamento de antigos: enquanto importa do WhatsApp
    // avisa que pode demorar; caso contrário, é a paginação rápida do banco.
    olderLoadingLabel() {
      return this.importingWhatsapp
        ? HISTORY_SCROLL.LOADING_WHATSAPP
        : HISTORY_SCROLL.LOADING_DB
    },
    isClosed() {
      return !!(
        this.selectedConversa && this.selectedConversa.status === 'fechada'
      )
    },
    // Verdadeiro quando a conexão da conversa aberta está conectada (ou,
    // se a conversa não tem id_conexao, se existe pelo menos uma conexão
    // conectada). Usado para travar o envio de mensagens quando o WhatsApp
    // do profissional não está pareado — evita request fadado a falhar.
    canSendMessage() {
      const conns = this.conexoes || []
      if (conns.length === 0) return false
      const selected = this.selectedConversa
      const selectedConexaoId = selected && selected.id_conexao
      if (selectedConexaoId) {
        return conns.some(
          (c) => c && c.id === selectedConexaoId && c.status === 'conectado'
        )
      }
      return conns.some((c) => c && c.status === 'conectado')
    },
    inputDisabled() {
      return this.isClosed || !this.canSendMessage
    },
  },
  watch: {
    // Pré-fetch silencioso da árvore do Drive do cliente assim que a conversa
    // vinculada abre — sem loading visível. Quando o usuário passar o mouse no
    // menu de Drive, a árvore já está no store (menu em cascata instantâneo).
    // forceUpdate:true (todo outro chamador de apiAawzDrive.carregar no repo
    // já usa true): shouldUseCache() compara um lastUpdate GLOBAL do módulo
    // (não por rootKey) contra o cache POR rootKey — se qualquer outro Drive
    // tiver sido buscado recentemente (outro cliente, outra tela), o cache
    // deste cliente pode ser servido como "fresco" mesmo desatualizado
    // (faltando pasta aninhada criada há pouco). false expôs exatamente esse
    // bug: só forçando o refresh (abrindo o modal do Drive) o menu voltava a
    // funcionar. forceUpdate:true elimina a janela de dado stale.
    idClienteApp: {
      immediate: true,
      handler(newId) {
        if (!newId) return
        apiAawzDrive.carregar({ forceUpdate: true, rootKey: newId })
      },
    },
    // Sincroniza o hasMore local com o has_more da CARGA INICIAL (store). Roda
    // quando a 1ª carga da conversa retorna (a action escreve mensagensHasMore
    // após o selectConversa resetá-lo para true). Assim, se o banco já veio
    // esgotado (false), o 1º scroll ao topo cai direto no import do WhatsApp —
    // sem paginar o banco à toa. Páginas de scroll usam before_id e NÃO mexem
    // em mensagensHasMore, então este watcher não sobrescreve o hasMore que o
    // applyOlderMessagesResult mantém durante o scroll.
    initialHasMore(value) {
      this.hasMore = value
    },
    selectedConversa(newConv, oldConv) {
      // Seed a partir do store (resetado para true no selectConversa); a carga
      // inicial ajusta via watcher initialHasMore quando retornar.
      this.hasMore = this.initialHasMore
      this.hasMoreWhatsapp = true
      this.loadingOlder = false
      this.importingWhatsapp = false
      // Salva o rascunho da conversa que estava aberta antes de trocar
      if (oldConv && oldConv.id) {
        const prev = (this.messageText || '').trim()
        if (prev) {
          this.$set(this.drafts, oldConv.id, this.messageText)
        } else if (this.drafts[oldConv.id]) {
          this.$delete(this.drafts, oldConv.id)
        }
      }
      // Carrega o rascunho da conversa nova (ou string vazia)
      if (newConv && newConv.id) {
        this.messageText = this.drafts[newConv.id] || ''
      } else {
        this.messageText = ''
      }
      // Foca o input automaticamente ao selecionar uma conversa — o usuário
      // pode começar a digitar direto sem precisar clicar no campo.
      this.$nextTick(() => {
        if (
          this.$refs.chatInput &&
          typeof this.$refs.chatInput.focus === 'function'
        ) {
          this.$refs.chatInput.focus()
        }
      })
      this.refreshVinculosTarefa()
    },
    mensagens(newVal, oldVal) {
      // Scroll to bottom when messages load for the first time (conversation opened)
      // or when new messages arrive at the end (not when loading older messages at top)
      if (
        !oldVal ||
        oldVal.length === 0 ||
        (newVal.length > oldVal.length && !this.loadingOlder)
      ) {
        this.scrollToBottom()
      }
    },
  },
  created() {
    // Refresh dos vínculos após uma tarefa ser criada a partir de uma
    // mensagem (emitido por task-detail-form). Sem polling — refresh pontual.
    GlobalBus.$on(RELOAD.ATENDIMENTO_TASK_LINKED, this.onTaskLinked)
  },
  mounted() {
    // When chat-window mounts (e.g. returning from Dashboard tab),
    // scroll to bottom if messages are already loaded
    if (this.mensagens && this.mensagens.length > 0) {
      this.scrollToBottom()
    }
    this.refreshVinculosTarefa()
  },
  beforeDestroy() {
    GlobalBus.$off(RELOAD.ATENDIMENTO_TASK_LINKED, this.onTaskLinked)
  },
  methods: {
    onSend(payload) {
      const text = payload ? payload.conteudo : this.messageText.trim()
      if (!text) return

      // Fire-and-forget: the Vuex action injects an optimistic bubble with a
      // "sending" clock and replaces it with the real message when the server
      // responds. The send button does NOT show a spinner anymore — feedback
      // lives inside the chat bubble, WhatsApp-style.
      this.messageText = ''
      this.$store.dispatch('atendimento/cancelReply')
      this.$store.dispatch('atendimento/setNoteMode', false)
      this.$store.dispatch('atendimento/enviarMensagem', {
        tipo: payload ? payload.tipo : 'texto',
        conteudo: text,
        is_note: payload ? payload.is_note : false,
        reply_to_message_id: payload ? payload.reply_to_message_id : null,
      })
      this.scrollToBottom()
    },

    async uploadAndSendMedia({
      id_conversa,
      file,
      caption,
      is_note,
      reply_to_message_id,
    }) {
      const tipo = detectMediaType(file)
      const uploadResult = await this.$store.dispatch(
        'atendimento/uploadMedia',
        {
          id_conversa,
          tipo,
          arquivo: file,
        }
      )
      if (!uploadResult || uploadResult.status !== 200 || !uploadResult.value) {
        const res = uploadResult || {
          status: 500,
          message: 'Erro ao enviar arquivo',
        }
        this.$showSnack({
          text: res.message || 'Erro ao enviar arquivo',
          color: 'error',
          button: true,
        })
        return
      }
      const payload = buildMediaMessagePayload({
        tipo,
        caption,
        is_note,
        reply_to_message_id,
        media: uploadResult.value,
      })
      await this.$store.dispatch('atendimento/enviarMensagem', payload)
      this.$store.dispatch('atendimento/cancelReply')
      this.scrollToBottom()
    },

    async onSendMedia({ file, caption, is_note, reply_to_message_id } = {}) {
      if (!file) return
      const id_conversa = this.selectedConversa
        ? this.selectedConversa.id
        : null
      if (!id_conversa) return
      if (this.sending) return

      this.sending = true
      try {
        await this.uploadAndSendMedia({
          id_conversa,
          file,
          caption,
          is_note,
          reply_to_message_id,
        })
      } finally {
        this.sending = false
      }
    },

    async onEditMessage({ id_mensagem, conteudo } = {}) {
      if (!id_mensagem || !conteudo) return
      const r = await this.$store.dispatch('atendimento/editarMensagem', {
        id_mensagem,
        conteudo,
      })
      this.$showSnack({
        text: (r && r.message) || 'Erro inesperado',
        color: r && r.status === 200 ? 'success' : 'error',
        button: true,
      })
    },

    onStartNovaConversa() {
      GlobalBus.$emit(MODAL.ATENDIMENTO_NOVA_CONVERSA)
    },

    onDeleteMessage(mensagem) {
      if (!mensagem || !mensagem.id) return
      if (!mensagem.is_note) return
      this.$showModal({
        title: 'Remover nota interna',
        text: 'Essa nota será apagada permanentemente. Deseja continuar?',
        type: 'danger',
        v2: true,
        center: true,
        buttons: [
          { text: 'Cancelar' },
          {
            text: 'Remover',
            action: async () => {
              const r = await this.$store.dispatch(
                'atendimento/removerMensagem',
                {
                  id_mensagem: mensagem.id,
                }
              )
              this.$showSnack({
                text: (r && r.message) || 'Erro inesperado',
                color: r && r.status === 200 ? 'success' : 'error',
                button: true,
              })
            },
          },
        ],
      })
    },

    onReply(mensagem) {
      this.$store.dispatch('atendimento/setReplyTo', {
        id: mensagem.id,
        whatsapp_message_id: mensagem.whatsapp_message_id || null,
        conteudo: mensagem.conteudo || '',
        remetente_nome: mensagem.remetente_nome || '',
        senderName:
          mensagem.direcao === 'saida' ? 'Você' : mensagem.remetente_nome || '',
      })
      // nextTick pra focar só depois que o reply-preview renderizou em cima
      // do input — evita "pular" foco por remount.
      this.$nextTick(() => {
        if (this.$refs.chatInput && this.$refs.chatInput.focus) {
          this.$refs.chatInput.focus()
        }
      })
    },

    onEdit(mensagem) {
      this.$store.dispatch('atendimento/setEditingMessage', mensagem)
    },

    onNote(mensagem) {
      this.$store.dispatch('atendimento/setNoteMode', true)
      this.$store.dispatch('atendimento/setReplyTo', {
        id: mensagem.id,
        whatsapp_message_id: mensagem.whatsapp_message_id || null,
        conteudo: mensagem.conteudo || '',
        remetente_nome: mensagem.remetente_nome || '',
        senderName:
          mensagem.direcao === 'saida' ? 'Você' : mensagem.remetente_nome || '',
      })
      this.$nextTick(() => {
        if (this.$refs.chatInput && this.$refs.chatInput.focus) {
          this.$refs.chatInput.focus()
        }
      })
    },

    async onRetry(mensagem) {
      if (!mensagem || !mensagem.conteudo) return
      await this.$store.dispatch('atendimento/enviarMensagem', {
        tipo: mensagem.tipo || 'texto',
        conteudo: mensagem.conteudo,
      })
    },

    // M1 — abre o modal de criação de tarefa pré-preenchido com o conteúdo
    // da mensagem e o vínculo external_source/external_id. Gated pela flag.
    // Também pré-preenche cliente vinculado e responsável do atendimento.
    onCreateTask(mensagem) {
      if (!this.taskFeatureEnabled) return
      if (!mensagem || !mensagem.id) return
      const conv = this.selectedConversa || {}
      const { startDate, dueDate } = buildDefaultTaskDates()
      GlobalBus.$emit(MODAL.TASK_DETAIL, {
        defaultTitle: buildTaskTitleFromMessage(mensagem),
        defaultDescription: buildTaskDescriptionFromMessage(mensagem),
        defaultClienteAppId: resolveClienteAppIdFromConversa(conv),
        defaultResponsavelProfissional: resolveResponsavelFromConversa(
          conv,
          this.$currentUser()
        ),
        defaultStartDate: startDate,
        defaultDueDate: dueDate,
        sprintId: null,
        folderId: null,
        externalSource: TASK_FROM_MESSAGE.EXTERNAL_SOURCE,
        externalId: String(mensagem.id),
      })
    },

    // Envia a mídia da mensagem para a pasta ESCOLHIDA pelo usuário no menu em
    // cascata (payload traz a mensagem e o nó de pasta selecionado). Toda a
    // orquestração (blob → File → upload) vive no serviço puro; aqui só
    // resolvemos o cliente vinculado e damos o feedback.
    async onSendToDrive({ mensagem, folder } = {}) {
      if (!mensagem || !mensagem.id) return
      const idClienteApp = resolveClienteAppIdFromConversa(this.selectedConversa)
      if (!idClienteApp) return
      this.$showSnack({ text: 'Enviando para o AAWZ Drive...', button: true })
      const r = await uploadMessageMediaToFolder({
        mensagem,
        idClienteApp,
        folder,
        deps: { apiAtendimento, apiAawzDrive },
      })
      this.$showSnack(r)
    },

    // Variante "e renomear": abre o modal de renomear do próprio Drive
    // (reaproveitado, modo promptName) para o usuário escolher o nome do
    // arquivo; ao confirmar, faz o upload na pasta escolhida com o nome custom.
    onSendToDriveRename({ mensagem, folder } = {}) {
      if (!mensagem || !mensagem.id) return
      const idClienteApp = resolveClienteAppIdFromConversa(this.selectedConversa)
      if (!idClienteApp) return
      GlobalBus.$emit(MODAL.RENAME_AAWZ_DRIVE_FILE, {
        action: 'promptName',
        rootKey: idClienteApp,
        scope: 'client',
        item: {
          name: mensagem.media_filename || 'arquivo',
          onConfirm: (customName) =>
            this.uploadToDriveWithCustomName({
              mensagem,
              folder,
              idClienteApp,
              customName,
            }),
        },
      })
    },

    async uploadToDriveWithCustomName({
      mensagem,
      folder,
      idClienteApp,
      customName,
    }) {
      this.$showSnack({ text: 'Enviando para o AAWZ Drive...', button: true })
      const r = await uploadMessageMediaToFolder({
        mensagem,
        idClienteApp,
        folder,
        customName,
        deps: { apiAtendimento, apiAawzDrive },
      })
      this.$showSnack(r)
    },

    // M1 — abre a tarefa vinculada em modo edição (badge clicado).
    onOpenTask(taskId) {
      if (!taskId) return
      GlobalBus.$emit(MODAL.TASK_DETAIL, { task: { id: taskId } })
    },

    // Carrega os vínculos tarefa↔mensagem da conversa atual (badges). A chamada
    // de API fica no componente; a action só comita os dados na store.
    async refreshVinculosTarefa() {
      if (!this.taskFeatureEnabled) return
      if (!this.selectedConversa || !this.selectedConversa.id) return
      const r = await apiAtendimento.listarVinculosTarefa({
        id_conversa: this.selectedConversa.id,
      })
      const list = r && r.status === 200 ? r.value : []
      this.$store.dispatch('atendimento/setVinculosTarefa', list)
    },

    onTaskLinked() {
      this.refreshVinculosTarefa()
    },

    // M2 — "carregar até achar": rola+destaca a nota; se ela não está no DOM,
    // carrega páginas anteriores (loadOlderMessages) até entrar no DOM,
    // limitado por MAX_ITER e por hasMore. Snack de erro se não localizar.
    async scrollToNote(messageId) {
      if (!messageId) return
      const selector =
        '[data-message-id="' + String(messageId).replaceAll('"', String.raw`\"`) + '"]'
      // Caminho rápido (síncrono): nota já no DOM → scroll imediato.
      if (this.$el.querySelector(selector)) {
        this.onScrollToMessage(messageId)
        return
      }
      const found = await this._loadUntilNoteFound(selector)
      if (found) {
        this.onScrollToMessage(messageId)
        return
      }
      this.$showSnack({
        text: NOTES_SECTION.NOT_FOUND,
        color: 'error',
        button: true,
      })
    },

    // Itera loadOlderMessages até o elemento entrar no DOM. Retorna true se
    // encontrou, false ao esgotar (MAX_ITER ou hasMore=false sem achar).
    // Nota interna vive no banco — nunca dispara import do WhatsApp aqui
    // (allowWhatsappImport=false), para não custar chamadas Evolution ao só
    // procurar uma nota já persistida.
    async _loadUntilNoteFound(selector) {
      const MAX_ITER = 30
      let i = 0
      while (i < MAX_ITER) {
        if (!this.hasMore) break
        const container = this.$el.querySelector('.chat-window__messages')
        if (!container) break
        await this.loadOlderMessages(container, { allowWhatsappImport: false })
        i++
        if (this.$el.querySelector(selector)) return true
      }
      return false
    },

    onScrollToMessage(messageId) {
      if (!messageId) return
      // Evita usar `el.scrollIntoView()` — ele sobe a cadeia de ancestrais
      // scrolláveis (incluindo o <v-container> do navbar global) e pode
      // cortar o header. Scrollamos manualmente só o container de mensagens.
      const scrollEl =
        this.$el.querySelector('.chat-window__messages') ||
        (this.$refs.messages &&
          this.$refs.messages.$el &&
          this.$refs.messages.$el.querySelector('.chat-window__messages'))
      // reply_to_message_id pode conter id numérico do banco (gestor
      // respondeu via UI) OU whatsapp_message_id (cliente respondeu
      // via WhatsApp e o webhook trouxe o quoted_msg_id) — tentar ambos.
      const safe = String(messageId).replaceAll('"', '\\"')
      const el =
        this.$el.querySelector('[data-message-id="' + safe + '"]') ||
        this.$el.querySelector('[data-whatsapp-id="' + safe + '"]')
      if (!scrollEl || !el) return
      const targetTop =
        el.offsetTop - scrollEl.clientHeight / 2 + el.clientHeight / 2
      scrollEl.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      })
      el.classList.add('message-highlight')
      setTimeout(() => {
        el.classList.remove('message-highlight')
      }, 3000)
    },

    onScrollMessages(event) {
      const container = event.target
      if (container.scrollTop > 50) return
      if (this.loadingOlder) return
      // Só bloqueia quando NÃO há mais nem no banco nem no WhatsApp. Enquanto
      // o WhatsApp puder ter mais (hasMoreWhatsapp), o scroll-até-o-topo
      // continua ativo mesmo com o banco esgotado (hasMore=false).
      if (!this.hasMore && !this.hasMoreWhatsapp) return
      if (!this.firstMessageId) return
      this.loadOlderMessages(container)
    },

    // Orquestra o scroll-até-o-topo: primeiro pagina o BANCO (rápido, sem
    // custo Evolution); quando o banco esgota (hasMore=false), cai no import
    // do WhatsApp (Evolution) para trazer o lote anterior — 1 import por gesto.
    // O guard anti-loop principal (loadingOlder) vive em onScrollMessages e em
    // _loadUntilNoteFound; aqui garantimos apenas 1 execução por vez.
    async loadOlderMessages(container, { allowWhatsappImport = true } = {}) {
      if (this.loadingOlder) return
      this.loadingOlder = true
      try {
        if (this.hasMore) {
          await this.paginateDatabase(container)
          return
        }
        if (allowWhatsappImport && this.hasMoreWhatsapp) {
          await this.importOlderFromWhatsapp(container)
        }
      } finally {
        this.loadingOlder = false
        this.importingWhatsapp = false
      }
    },

    // Pagina o banco (before_id) e faz o prepend preservando a posição.
    async paginateDatabase(container) {
      const prevScrollHeight = container.scrollHeight
      const r = await this.$store.dispatch('atendimento/listarMensagens', {
        id_conversa: this.selectedConversa.id,
        before_id: this.firstMessageId,
      })
      this.applyOlderMessagesResult(r, container, prevScrollHeight)
    },

    // Importa o lote anterior do WhatsApp (Evolution) e, se trouxe mensagens
    // novas, repagina o banco para trazê-las (persistidas com dedup) e fazer o
    // prepend. Falha do Evolution (timeout comum) → snack + mantém
    // hasMoreWhatsapp=true para permitir retry no próximo scroll.
    async importOlderFromWhatsapp(container) {
      this.importingWhatsapp = true
      const resp = await apiAtendimento.importarAnteriores({
        id_conversa: this.selectedConversa.id,
        before_ts: this.oldestMessageTs,
        batch: IMPORT_ANTERIORES_BATCH,
      })
      if (!resp || resp.status !== 200 || !resp.value) {
        this.$showSnack({
          text: (resp && resp.message) || HISTORY_SCROLL.IMPORT_ERROR,
          color: 'error',
          button: true,
        })
        return
      }
      // Só marca o fim (hasMoreWhatsapp=false) com um retorno bem-sucedido do
      // backend — nunca por erro (mantém retry disponível).
      this.hasMoreWhatsapp = resp.value.has_more_whatsapp !== false
      const imported = resp.value.imported || 0
      if (imported > 0) {
        await this.paginateDatabase(container)
      }
    },

    applyOlderMessagesResult(r, container, prevScrollHeight) {
      if (!r || r.status !== 200) return
      const newMsgs = (r.value && r.value.mensagens) || []
      this.hasMore = (r.value && r.value.has_more) || false
      if (newMsgs.length === 0) return
      this.$store.dispatch('atendimento/prependMensagens', newMsgs)
      this.$nextTick(() => {
        container.scrollTop = container.scrollHeight - prevScrollHeight
      })
    },

    onImportHistory() {
      this.$emit('import-history')
    },

    onCloseConversa() {
      this.$emit('close-conversa')
    },

    async onAtribuir() {
      if (this.loadingAction) return
      if (!this.selectedConversa) return
      this.loadingAction = true
      try {
        const r = await this.$store.dispatch('atendimento/atribuirConversa', {
          id_conversa: this.selectedConversa.id,
        })
        this.$showSnack({
          text: (r && r.message) || 'Erro inesperado',
          color: r && r.status === 200 ? 'success' : 'error',
          button: true,
        })
      } finally {
        this.loadingAction = false
      }
    },

    async onFechar() {
      if (this.loadingAction) return
      if (!this.selectedConversa) return
      this.loadingAction = true
      try {
        const r = await this.$store.dispatch('atendimento/fecharConversa', {
          id_conversa: this.selectedConversa.id,
        })
        this.$showSnack({
          text: (r && r.message) || 'Erro inesperado',
          color: r && r.status === 200 ? 'success' : 'error',
          button: true,
        })
      } finally {
        this.loadingAction = false
      }
    },

    onTransferir() {
      if (!this.selectedConversa) return
      GlobalBus.$emit(MODAL.ATENDIMENTO_TRANSFERIR_CONVERSA, {
        id_conversa: this.selectedConversa.id,
        mode: 'transferir',
      })
    },

    onEscalar() {
      if (!this.selectedConversa) return
      GlobalBus.$emit(MODAL.ATENDIMENTO_TRANSFERIR_CONVERSA, {
        id_conversa: this.selectedConversa.id,
        mode: 'escalar',
      })
    },

    onInsertQuickReply(content) {
      this.messageText = content
    },

    async onReact({ mensagem, emoji } = {}) {
      if (!mensagem || !mensagem.id) return
      const r = await this.$store.dispatch('atendimento/reagirMensagem', {
        id_mensagem: mensagem.id,
        emoji,
      })
      if (!r || r.status !== 200) {
        this.$showSnack({
          text: (r && r.message) || 'Erro ao enviar reação',
          color: 'error',
          button: true,
        })
      }
    },

    onInputFocus() {
      if (!this.selectedConversa) return
      if (this.selectedConversa.mensagens_nao_lidas > 0) {
        this.$store.dispatch('atendimento/marcarComoLida', {
          id_conversa: this.selectedConversa.id,
        })
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (
          this.$refs.messages &&
          typeof this.$refs.messages.scrollToBottom === 'function'
        ) {
          this.$refs.messages.scrollToBottom()
        }
      })
    },

    onOpenProfile(payload) {
      this.$refs.profilePicModal.open(payload)
    },
  },
}
</script>

<template>
  <div class="chat-window d-flex flex-column chat-window-root">
    <template v-if="hasConversa">
      <ChatWindowHeader
        :selected-conversa="selectedConversa"
        :conexoes="conexoes"
        :show-close-conversa="showCloseConversa"
        @open-profile="onOpenProfile"
        @atribuir="onAtribuir"
        @transferir="onTransferir"
        @escalar="onEscalar"
        @fechar="onFechar"
        @import-history="onImportHistory"
        @close-conversa="onCloseConversa"
      />

      <ChatWindowMessages
        ref="messages"
        :mensagens="visibleMensagens"
        :loading-older="loadingOlder"
        :older-loading-label="olderLoadingLabel"
        :beginning-of-conversation="beginningOfConversation"
        :loading="loadingMensagens"
        :linked-task-by-message-id="linkedTaskByMessageId"
        :has-linked-client="hasLinkedClient"
        :id-cliente-app="idClienteApp"
        @scroll="onScrollMessages"
        @retry="onRetry"
        @reply="onReply"
        @edit="onEdit"
        @react="onReact"
        @note="onNote"
        @delete="onDeleteMessage"
        @create-task="onCreateTask"
        @send-to-drive="onSendToDrive"
        @send-to-drive-rename="onSendToDriveRename"
        @open-task="onOpenTask"
        @scroll-to-message="onScrollToMessage"
      />

      <ChatInput
        ref="chatInput"
        :value="messageText"
        :disabled="inputDisabled"
        :sending="false"
        @input="messageText = $event"
        @send="onSend"
        @send-media="onSendMedia"
        @edit-message="onEditMessage"
        @insert-quick-reply="onInsertQuickReply"
        @focus="onInputFocus"
      />
    </template>

    <ChatWindowEmptyState
      v-else
      :conexao-cta="conexaoCta"
      :can-send-message="canSendMessage"
      @start-nova-conversa="onStartNovaConversa"
    />

    <ModalProfilePicture ref="profilePicModal" />
  </div>
</template>

<style lang="sass" scoped>
.chat-window-root
  height: 100%

.chat-window
  background-color: var(--panel-bg)

.chat-window__empty
  background-color: var(--panel-bg)

.chat-window__empty-hint
  max-width: 420px
</style>
